import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, Lightbulb, BookOpen, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } })
        .then((r) => { setResults(r.data); setOpen(true); })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const hasResults = results && (results.employees.length || results.skills.length || results.courses.length);

  return (
    <div className="relative flex-1 max-w-md" ref={boxRef}>
      <Search className="h-4 w-4 absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10" />
      <Input
        data-testid="global-search-input"
        placeholder={t("search.placeholder")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        className="rounded-none h-9 ps-9 text-[13px] bg-slate-50"
      />
      {open && results && (
        <div className="absolute top-10 left-0 right-0 bg-white border border-border shadow-md z-50 max-h-96 overflow-y-auto"
          data-testid="global-search-results">
          {!hasResults && (
            <p className="text-[13px] text-muted-foreground text-center py-5">No results for “{q}”</p>
          )}
          {results.employees.length > 0 && (
            <div>
              <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Employees</p>
              {results.employees.map((e) => (
                <button key={e.id} data-testid={`search-result-emp-${e.id}`}
                  onClick={() => { setOpen(false); setQ(""); navigate(`/employees/${e.id}`); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 text-left">
                  <User className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-[13px] text-slate-800">{e.name}</span>
                  <span className="text-[11px] text-muted-foreground ml-auto">{e.title} · {e.department}</span>
                </button>
              ))}
            </div>
          )}
          {results.skills.length > 0 && (
            <div>
              <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Skills</p>
              {results.skills.map((s) => (
                <div key={s.name} className="flex items-center gap-2.5 px-3 py-2">
                  <Lightbulb className="h-3.5 w-3.5 text-teal-600" />
                  <span className="text-[13px] text-slate-800">{s.name}</span>
                  <span className="text-[11px] font-mono text-muted-foreground ml-auto">{s.category}</span>
                </div>
              ))}
            </div>
          )}
          {results.courses.length > 0 && (
            <div className="pb-2">
              <p className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Coursera Courses</p>
              {results.courses.map((c) => (
                <a key={c.id} href={c.url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50">
                  <BookOpen className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-[13px] text-slate-800 truncate">{c.title}</span>
                  <ExternalLink className="h-3 w-3 text-muted-foreground ml-auto shrink-0" />
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
